import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { Page } from '../lib/api';
import type { OffboardingView } from '../lib/types';
import type { StaffSummary } from '../lib/session';
import { useDebounced, useResource } from '../lib/useResource';
import { useSession } from '../lib/session';
import { Card, Empty, Loading, Notice, StatusPill, exactly, when } from '../components/ui';

export function Offboarding() {
  const { can } = useSession();
  const [staffQuery, setStaffQuery] = useState('');
  const [staffId, setStaffId] = useState('');
  const [reason, setReason] = useState('');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [wipeDevices, setWipeDevices] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const debouncedQuery = useDebounced(staffQuery);

  const requests = useResource(() => api.get<OffboardingView[]>('/api/v1/offboarding'), []);
  const staff = useResource(
    () =>
      can('TECH_ADMIN')
        ? api.get<Page<StaffSummary>>('/api/v1/staff', { query: debouncedQuery || undefined, size: 25 })
        : Promise.resolve(null),
    [debouncedQuery],
  );

  async function submit() {
    setBusy(true);
    setMessage(null);
    try {
      await api.post('/api/v1/offboarding', {
        staffId,
        reason: reason.trim(),
        effectiveAt: effectiveAt ? new Date(effectiveAt).toISOString() : null,
        wipeDevices,
      });
      setMessage({
        kind: 'ok',
        text: effectiveAt
          ? 'Offboarding scheduled. It will run automatically at the effective time.'
          : 'Offboarding executed. The account is suspended, its sessions revoked, and its devices locked.',
      });
      setStaffId('');
      setReason('');
      setWipeDevices(false);
      requests.reload();
    } catch (caught) {
      setMessage({ kind: 'error', text: caught instanceof ApiError ? caught.message : 'The request failed' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Offboarding</h1>
        <p>
          One instruction that suspends a leaver's account, revokes every session it holds, and
          locks and signs out every device assigned to them.
        </p>
      </div>

      {message && <Notice kind={message.kind}>{message.text}</Notice>}

      {can('TECH_ADMIN', 'HR_REVIEWER') && (
        <Card title="Start an offboarding">
          <div className="filters">
            <label className="field grow">
              <span>Find the employee</span>
              <input
                type="search"
                value={staffQuery}
                placeholder="Name or email"
                onChange={(event) => setStaffQuery(event.target.value)}
              />
            </label>

            <label className="field grow">
              <span>Employee</span>
              <select value={staffId} onChange={(event) => setStaffId(event.target.value)}>
                <option value="">Select…</option>
                {staff.data?.content
                  .filter((person) => person.status === 'ACTIVE')
                  .map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.fullName} · {person.email}
                    </option>
                  ))}
              </select>
            </label>

            <label className="field">
              <span>
                Effective
                <span className="hint">Leave blank to run now</span>
              </span>
              <input type="date" value={effectiveAt} onChange={(event) => setEffectiveAt(event.target.value)} />
            </label>
          </div>

          <label className="field">
            <span>
              Reason
              <span className="hint">Recorded in the audit trail and on every command it issues.</span>
            </span>
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} />
          </label>

          <label className="field" style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={wipeDevices}
              onChange={(event) => setWipeDevices(event.target.checked)}
              style={{ width: 'auto', marginTop: 3 }}
            />
            <span style={{ fontWeight: 400, fontSize: 13 }}>
              Also request removal of company data from their devices.
              <span className="hint">
                This raises a wipe request per device; it still needs two administrators to approve
                before anything is erased.
              </span>
            </span>
          </label>

          <button type="button" disabled={busy || !staffId || reason.trim().length < 5} onClick={submit}>
            {busy ? 'Working…' : effectiveAt ? 'Schedule offboarding' : 'Offboard now'}
          </button>
        </Card>
      )}

      <Card title="Offboarding history">
        {requests.loading && !requests.data && <Loading />}
        {requests.error && <Notice kind="error">{requests.error}</Notice>}
        {requests.data?.length === 0 && <Empty>No offboarding requests have been raised.</Empty>}

        {requests.data && requests.data.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Employee</th><th>Status</th><th>Effective</th><th>Reason</th>
                  <th>Wipe</th><th>Executed</th><th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {requests.data.map((request) => (
                  <tr key={request.id}>
                    <td><strong>{request.staffName ?? request.staffId}</strong></td>
                    <td><StatusPill value={request.status} /></td>
                    <td title={exactly(request.effectiveAt)}>{when(request.effectiveAt)}</td>
                    <td style={{ fontSize: 12.5, maxWidth: '30ch' }}>{request.reason}</td>
                    <td>{request.wipeDevices ? 'requested' : 'no'}</td>
                    <td>{request.executedAt ? when(request.executedAt) : '—'}</td>
                    <td style={{ maxWidth: '30ch' }}>
                      {request.outcome ? (
                        <details>
                          <summary style={{ cursor: 'pointer', fontSize: 12.5, color: 'var(--info)' }}>
                            What happened
                          </summary>
                          <pre className="evidence" style={{ marginTop: 8, maxHeight: 180, fontSize: 11.5 }}>
                            {JSON.stringify(request.outcome, null, 2)}
                          </pre>
                        </details>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
